<script>
/**
 * Card rendering of ag-grid rows, derived entirely from a page's existing
 * `columnDefs` + the row objects. Each card lists field/value pairs, reusing
 * the column's `cellRenderer` (HTML) or `valueFormatter` so badges and money
 * formatting look the same as in the grid. Paginates client-side so large
 * result sets stay usable on mobile.
 */
export default {
  name: "DataCardList",
  props: {
    columnDefs: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    titleField: { type: String, default: "" },
    pageSize: { type: Number, default: 12 },
    emptyText: { type: String, default: "No records to display" },
    loading: { type: Boolean, default: false },
  },
  emits: ["row-click"],
  data() {
    return { page: 1 };
  },
  computed: {
    leafColumns() {
      // Flatten grouped column defs (parents carry `children`, not a field).
      const flat = [];
      const walk = (defs) => {
        (defs || []).forEach((c) => {
          if (!c) return;
          if (Array.isArray(c.children)) walk(c.children);
          else flat.push(c);
        });
      };
      walk(this.columnDefs);
      return flat;
    },
    fields() {
      // Data columns — skip the selection/checkbox column.
      return this.leafColumns.filter(
        (c) => c.field && !c.checkboxSelection && !c.headerCheckboxSelection
      );
    },
    actionCols() {
      // Field-less columns with an HTML cellRenderer are action/button columns
      // (edit/sign/delete/view). Render them in a card footer so card view keeps
      // the grid's row actions; clicks carry the native event for the page's
      // [data-action] target detection.
      return this.leafColumns.filter(
        (c) =>
          !c.field &&
          typeof c.cellRenderer === "function" &&
          !c.checkboxSelection &&
          !c.headerCheckboxSelection &&
          !c.headerComponent
      );
    },
    titleCol() {
      if (this.titleField) {
        return this.fields.find((c) => c.field === this.titleField) || this.fields[0] || null;
      }
      return this.fields[0] || null;
    },
    bodyFields() {
      const tf = this.titleCol && this.titleCol.field;
      return this.fields.filter((c) => c.field !== tf);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.rows.length / this.pageSize));
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.rows.slice(start, start + this.pageSize);
    },
  },
  watch: {
    rows() {
      this.page = 1;
    },
    totalPages(n) {
      if (this.page > n) this.page = n;
    },
  },
  methods: {
    // Mirror an ag-grid cell-click event: `{ data, event }`, so pages can read
    // the row (`$event.data`) or detect an action button (`$event.event.target`).
    onCardClick(row, event) {
      this.$emit("row-click", { data: row, event });
    },
    renderCell(col, row) {
      const value = row ? row[col.field] : undefined;
      const params = { value, data: row, colDef: col, node: { data: row } };
      try {
        if (typeof col.cellRenderer === "function") {
          const out = col.cellRenderer(params);
          if (typeof out === "string") return out;
        }
        if (typeof col.valueFormatter === "function") {
          const out = col.valueFormatter(params);
          return this.escape(out == null ? "" : String(out));
        }
      } catch (e) {
        /* fall through to raw value */
      }
      return this.escape(value == null || value === "" ? "—" : String(value));
    },
    escape(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    prev() {
      if (this.page > 1) this.page -= 1;
    },
    next() {
      if (this.page < this.totalPages) this.page += 1;
    },
  },
};
</script>

<template>
  <div class="ws-cards">
    <div v-if="loading" class="ws-cards__loading">
      <div class="ws-cards__spinner" role="status" aria-live="polite">
        <span class="visually-hidden">Loading…</span>
      </div>
    </div>

    <div v-else-if="!rows.length" class="ws-cards__empty">
      <i class="bx bx-folder-open"></i>
      <span>{{ emptyText }}</span>
    </div>

    <template v-else>
      <div class="ws-cards__grid">
        <div
          v-for="(row, idx) in pagedRows"
          :key="row.id || idx"
          class="ws-card"
          @click="onCardClick(row, $event)"
        >
          <div v-if="titleCol" class="ws-card__head">
            <span class="ws-card__head-label">{{ titleCol.headerName || titleCol.field }}</span>
            <span class="ws-card__head-val" v-html="renderCell(titleCol, row)"></span>
          </div>
          <div class="ws-card__body">
            <div v-for="col in bodyFields" :key="col.field" class="ws-card__row">
              <span class="ws-card__label">{{ col.headerName || col.field }}</span>
              <span class="ws-card__val" v-html="renderCell(col, row)"></span>
            </div>
          </div>
          <div v-if="actionCols.length" class="ws-card__actions">
            <span
              v-for="(col, i) in actionCols"
              :key="'a' + i"
              class="ws-card__action"
              v-html="renderCell(col, row)"
            ></span>
          </div>
        </div>
      </div>

      <div v-if="totalPages > 1" class="ws-cards__pager">
        <button type="button" class="btn btn-sm btn-light" :disabled="page === 1" @click="prev">
          <i class="bx bx-chevron-left"></i>
        </button>
        <span class="ws-cards__pageinfo">Page {{ page }} of {{ totalPages }}</span>
        <button
          type="button"
          class="btn btn-sm btn-light"
          :disabled="page === totalPages"
          @click="next"
        >
          <i class="bx bx-chevron-right"></i>
        </button>
      </div>
    </template>
  </div>
</template>

<style>
.ws-cards__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.85rem;
}
.ws-card {
  border: 1px solid #e9ecef;
  border-radius: 14px;
  background: #fff;
  padding: 0.85rem 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.ws-card:hover {
  border-color: #f1d9bf;
  box-shadow: 0 6px 18px rgba(245, 124, 0, 0.12);
}
.ws-card__head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-bottom: 0.6rem;
  margin-bottom: 0.6rem;
  border-bottom: 1px solid #f1f1f1;
}
.ws-card__head-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #92400e;
  font-weight: 600;
}
.ws-card__head-val {
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
}
.ws-card__row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.2rem 0;
  font-size: 0.85rem;
}
.ws-card__label {
  color: #6c757d;
  flex: 0 0 auto;
}
.ws-card__val {
  color: #212529;
  text-align: right;
  word-break: break-word;
}
.ws-card__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
  margin-top: 0.65rem;
  padding-top: 0.6rem;
  border-top: 1px solid #f1f1f1;
}
.ws-card__action {
  display: inline-flex;
  gap: 0.4rem;
}
.ws-cards__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  padding: 3rem 1rem;
}
.ws-cards__spinner {
  width: 2.4rem;
  height: 2.4rem;
  border: 0.25rem solid rgba(245, 124, 0, 0.2);
  border-top-color: #f57c00;
  border-radius: 50%;
  animation: ws-cards-spin 0.7s linear infinite;
}
@keyframes ws-cards-spin {
  to {
    transform: rotate(360deg);
  }
}
.ws-cards__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 3rem 1rem;
  color: #adb5bd;
}
.ws-cards__empty i {
  font-size: 2.2rem;
}
.ws-cards__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  margin-top: 1rem;
}
.ws-cards__pageinfo {
  font-size: 0.85rem;
  color: #6c757d;
}
</style>
